import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { RumInitConfiguration, datadogRum } from '@datadog/browser-rum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	AJSService,
	ApiVersionService,
	AuthService,
	AuthenticationInfo,
	DialogService,
	FeedbackService,
	FlyoutService,
	HttpCancelService,
	NavigationService,
	ReportService,
	SocketService,
} from '@app/core/services';
import { ReportModeEnum } from '@app/enums';
import { PopoverService } from '@app/shared/components/popover/popover.service';
import { LinqSocketsProgressComponent } from '@app/shared/sockets/components';
import { environment } from '@environments/environment';
import { LoadingComponent } from '../../components/loading/loading.component';
import { DesignComponent } from '../design/design.component';

declare interface Window {
	pendo: any;
}

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	standalone: true,
	imports: [DesignComponent, RouterOutlet, LoadingComponent, LinqSocketsProgressComponent],
})
export class AppComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject();

	constructor(
		private readonly ajsService: AJSService,
		private readonly apiVersionService: ApiVersionService,
		private readonly authService: AuthService,
		private readonly feedbackService: FeedbackService,
		private readonly httpCancelService: HttpCancelService,
		private readonly navigationService: NavigationService,
		private readonly popoverService: PopoverService,
		private readonly router: Router,
		private readonly reportService: ReportService,
		private readonly dialogService: DialogService,
		private readonly socketService: SocketService,
		private readonly flyoutService: FlyoutService
	) {}

	ngOnInit(): void {
		const ddConfig: RumInitConfiguration = {
			applicationId: environment.DD_APP_ID,
			clientToken: environment.DD_TOKEN,
			site: environment.DD_SITE,
			service: environment.DD_SERVICE,
			version: environment.BUILD_ID,
			env: environment.ENV,
			sampleRate: 100, // percentage of all sessions collected
			replaySampleRate: 100,
			trackInteractions: true,
			defaultPrivacyLevel: 'mask-user-input',
		};

		datadogRum.init(ddConfig);

		if (environment.production) {
			datadogRum.startSessionReplayRecording();
		}

		this.apiVersionService.shouldRefresh$.pipe(takeUntil(this.destroy$)).subscribe((shouldRefresh: boolean) => {
			if (shouldRefresh) {
				this.feedbackService.clear(null, 'warn');
				this.feedbackService.warn('The site has been updated, please save your work and refresh your browser');
			}
		});

		this.authService.authentication$.pipe(takeUntil(this.destroy$)).subscribe((authentication: AuthenticationInfo) => {
			if (authentication.userId) {
				datadogRum.addRumGlobalContext('usr', {
					name: authentication.userName,
					unit: authentication.unitId,
					apiVersion: this.apiVersionService.getApiVersion(),
				});
			} else {
				datadogRum.setRumGlobalContext({ usr: null });
			}
		});

		this.authService.authentication$.pipe(takeUntil(this.destroy$)).subscribe((authentication: AuthenticationInfo) => {
			if (!authentication.userId) {
				return;
			}
			// Pendo stuff:
			// create visitor object
			const visitor: any = {
				id: authentication.userId, // Required if user is logged in
				email: authentication.email, // Optional
				role: authentication.role, // Optional
				// You can add any additional visitor level key-values here,
				// as long as it's not one of the above reserved names.
				username: authentication.userName,
				// eslint-disable-next-line @typescript-eslint/naming-convention
				app_name: 'School Bookkeeping',
				// eslint-disable-next-line @typescript-eslint/naming-convention
				full_name: null,
				districtname: null,
			};

			if (authentication.fullName && authentication.fullName.length > 0) {
				visitor.full_name = authentication.fullName;
			}

			// create account object
			const account: any = {
				id: authentication.unitName, // Highly recommended
				// name:         // Optional
				// planLevel:    // Optional
				// planPrice:    // Optional
				// creationDate: // Optional

				// You can add any additional account level key-values here,
				// as long as it's not one of the above reserved names.
				districtid: authentication.unitId,
				statecode: authentication.homeState,
				pendoprofile: authentication.pendoProfile,
				// eslint-disable-next-line @typescript-eslint/naming-convention
				app_name: 'School Bookkeeping',
				districtname: null,
			};
			if (authentication.unitName && authentication.unitName.length > 0) {
				account.districtname = authentication.unitName;
				visitor.districtname = authentication.unitName;
			}

			// init pendo
			(window as unknown as Window).pendo.initialize({
				visitor,
				account,
			});
		});

		this.navigationService.route$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			const ajsRoot = document.getElementById('ajs-root');
			if (ajsRoot) {
				ajsRoot.style.display = this.isAJS ? null : 'none';
			}
			this.popoverService.close();
			this.feedbackService.clearAll();
			this.dialogService.closeAll();
			this.flyoutService.close();
		});

		// Update feedbackService context to handle `auto` context.
		this.flyoutService.open$.pipe(takeUntil(this.destroy$)).subscribe((open) => {
			this.popoverService.close();
			this.feedbackService.computeContext('flyout', open);

			// Clear all flyout errors when the flyout closes.
			if (!open) {
				this.feedbackService.clear(undefined, 'error', 'flyout');
				this.feedbackService.clear(undefined, 'warn', 'flyout');
			}
		});

		// Update feedbackService context to handle `auto` context.
		this.dialogService.modalOpened$.pipe(takeUntil(this.destroy$)).subscribe((open) => {
			this.feedbackService.computeContext('modal', open);

			// Clear all modal errors when the modal closes.
			if (!open) {
				this.feedbackService.clear(undefined, 'error', 'modal');
				this.feedbackService.clear(undefined, 'warn', 'modal');
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	/**
	 * Render Angular when url does not match angularJS
	 */
	public get isAJS(): boolean {
		return this.ajsService.isAJSRoute(this.router.url);
	}

	/**
	 * Routes that should render without the header/menu chrome, e.g. automated health checks
	 */
	public get isMinimalRoute(): boolean {
		return this.router.url.startsWith('/health/live');
	}

	// prevent auto dismiss when reports are finished
	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	processFinish(event: any): void {
		const { process } = event;

		if (process.Data && (process.Data.BlobData || process.Data.HyperlinkData)) {
			// default is auto-dismiss timer, prevent for reports and links
			event.preventDefault();
		}
	}

	// download report on click
	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	processClick(event: any): void {
		const { process } = event;

		if (process.Data && process.Data.BlobData) {
			const { Data, FileName, ContentType } = process.Data.BlobData;
			const blobData = this.reportService.base64ToBlobData(Data);

			this.reportService.downloadBlob(blobData, FileName, ContentType, ReportModeEnum.Download);
		} else if (process.Data && process.Data.HyperlinkData) {
			const { TargetURL } = process.Data.HyperlinkData;
			window.location.href = TargetURL;
		}
	}
}
